import { type ComponentPropsWithoutRef } from 'react';

import { useId } from '../../core/hooks/useId.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DOMProps } from '../../core/types/dom.js';

/** @internal */
export interface AriaDialogProps extends DOMProps, AriaLabelingProps {
  /**
   * The accessibility role for the dialog.
   * @defaultValue 'dialog'
   */
  role?: 'dialog' | 'alertdialog';
}

/** @internal */
export interface DialogAria {
  /** Props for the dialog container element. */
  dialogProps: ComponentPropsWithoutRef<'dialog'>;

  /** Props for the dialog title element. */
  titleProps: ComponentPropsWithoutRef<'title'>;
}

/**
 * Provides the behavior and accessibility implementation for a dialog component.
 * A dialog is an overlay shown above other content in an application.
 * @internal
 */
export function useDialog(props: AriaDialogProps): DialogAria {
  const {
    role = 'dialog',
    'aria-labelledby': ariaLabelledBy,
    ...remainingProps
  } = props;
  const uniqueId = useId();

  const titleId: string | undefined = props['aria-label']
    ? undefined
    : uniqueId;

  return {
    dialogProps: {
      role,
      tabIndex: -1,
      'aria-labelledby': ariaLabelledBy || titleId,
      ...remainingProps,
    },
    titleProps: {
      id: titleId,
    },
  };
}
